import { useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { Play, Pause, RotateCcw, Star } from "lucide-react";
import { ToothMascot } from "@/components/site/ToothMascot";

const TOTAL = 120;
const ZONES = ["Top-left teeth", "Top-right teeth", "Bottom-left teeth", "Bottom-right teeth"];

const Ring = ({ left }) => {
  const r = 120, c = 2 * Math.PI * r, pct = left / TOTAL;
  return (
    <svg viewBox="0 0 280 280" className="absolute inset-0 h-full w-full -rotate-90" aria-hidden="true">
      <circle cx="140" cy="140" r={r} stroke="#e2e8f0" strokeWidth="18" fill="none" />
      <circle cx="140" cy="140" r={r} stroke="#0d9488" strokeWidth="18" fill="none" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c * (1 - pct)} style={{ transition: "stroke-dashoffset 1s linear" }} />
    </svg>
  );
};

export const BrushTimer = () => {
  const [left, setLeft] = useState(TOTAL);
  const [running, setRunning] = useState(false);
  const [stars, setStars] = useState(() => Number(localStorage.getItem("tb_stars") || 0));
  const timer = useRef(null);

  useEffect(() => {
    if (!running) return undefined;
    timer.current = setInterval(() => setLeft((l) => l - 1), 1000);
    return () => clearInterval(timer.current);
  }, [running]);

  useEffect(() => {
    if (left > 0) return;
    setRunning(false);
    const s = stars + 1;
    setStars(s); localStorage.setItem("tb_stars", String(s));
    confetti({ particleCount: 180, spread: 100, origin: { y: 0.6 } });
  }, [left]); // eslint-disable-line react-hooks/exhaustive-deps

  const zone = Math.min(3, Math.floor((TOTAL - left) / 30));
  const finished = left <= 0;
  return (
    <div className="rounded-[2.5rem] bg-[#ebfbfa] p-8 sm:p-10" data-testid="brushing-timer">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold sm:text-3xl">2-minute brushing challenge</h2>
        <span className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-sm font-bold text-slate-700" data-testid="brushing-stars">
          <Star className="h-4 w-4 fill-[#f5b82e] text-[#f5b82e]" />{stars}
        </span>
      </div>
      <div className="relative mx-auto mt-8 h-64 w-64 sm:h-72 sm:w-72">
        <Ring left={left} />
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className={running ? "animate-squish" : ""}><ToothMascot mood={finished ? "wow" : "happy"} className="w-20" /></div>
          <p className="font-display mt-2 text-4xl font-bold text-slate-800" data-testid="brushing-time-left">
            {Math.floor(Math.max(left, 0) / 60)}:{String(Math.max(left, 0) % 60).padStart(2, "0")}
          </p>
        </div>
      </div>
      <p className="font-display mt-6 text-center text-xl font-semibold text-teal-700" data-testid="brushing-zone">
        {finished ? "Sparkling clean! You earned a star!" : running ? `Now brush: ${ZONES[zone]}` : "Grab your brush and press start!"}
      </p>
      <div className="mt-6 flex justify-center gap-3">
        <button onClick={() => (finished ? (setLeft(TOTAL), setRunning(true)) : setRunning(!running))} data-testid="brushing-timer-start"
          className="btn-pop shadow-chunky-coral inline-flex items-center gap-2 rounded-full bg-[#ff6b6b] px-7 py-3.5 font-bold text-white">
          {running ? <><Pause className="h-5 w-5" />Pause</> : <><Play className="h-5 w-5" />{finished ? "Again!" : "Start"}</>}
        </button>
        <button onClick={() => { setRunning(false); setLeft(TOTAL); }} data-testid="brushing-timer-reset"
          className="btn-pop inline-flex items-center gap-2 rounded-full border-2 border-slate-800 bg-white px-6 py-3.5 font-bold text-slate-800"><RotateCcw className="h-5 w-5" />Reset</button>
      </div>
    </div>
  );
};
